import classNames from 'classnames';
import { useEvent } from 'rc-util';
import pickAttrs from 'rc-util/lib/pickAttrs';
import * as React from 'react';
import { useXProviderContext } from '@ant-design/x/es/x-provider';
import Bubble, { BubbleContext } from '@ant-design/x/es/bubble/Bubble';
import type { BubbleRef } from '@ant-design/x/es/bubble/Bubble';
import useListData from '@ant-design/x/es/bubble/hooks/useListData';
import type { BubbleProps } from '@ant-design/x/es/bubble/interface';
import useStyle from '@ant-design/x/es/bubble/style';

export interface BubbleListRef {
  nativeElement: HTMLDivElement;
  scrollTo: (info: {
    offset?: number;
    key?: string | number;
    behavior?: ScrollBehavior;
    block?: ScrollLogicalPosition;
  }) => void;
}

export type BubbleDataType = BubbleProps<any> & {
  key?: string | number;
  role?: string;
};

export type RoleType = Partial<Omit<BubbleProps<any>, 'content'>>;

export type RolesType =
  | Record<string, RoleType>
  | ((bubbleDataP: BubbleDataType, index: number) => RoleType);

export interface BubbleListProps extends React.HTMLAttributes<HTMLDivElement> {
  prefixCls?: string;
  rootClassName?: string;
  items?: BubbleDataType[];
  autoScroll?: boolean;
  roles?: RolesType;
}

interface BubbleListItemProps extends BubbleProps {
  _key?: BubbleDataType['key'];
}

const BubbleListItem: React.ForwardRefRenderFunction<
  Record<string, BubbleRef>,
  BubbleListItemProps
> = ({ _key, ...restProps }, ref) => (
  <Bubble
    {...restProps}
    _key={_key}
    ref={(node) => {
      if (node) {
        (ref as React.RefObject<Record<string, BubbleRef>>).current[_key!] = node;
      } else {
        delete (ref as React.RefObject<Record<string, BubbleRef>>).current?.[_key!];
      }
    }}
  />
);

const MemoBubbleListItem = React.memo(React.forwardRef(BubbleListItem));

const TOLERANCE = 1;

const BubbleList: React.ForwardRefRenderFunction<BubbleListRef, BubbleListProps> = (props, ref) => {
  const {
    prefixCls: customizePrefixCls,
    rootClassName,
    className,
    items,
    autoScroll = true,
    roles,
    ...restProps
  } = props;
  const domProps = pickAttrs(restProps, {
    attr: true,
    aria: true,
  });

  // ============================= Refs =============================
  const listRef = React.useRef<HTMLDivElement>(null);

  const bubbleRefs = React.useRef<Record<string, BubbleRef>>({});

  // ============================ Prefix ============================
  const { getPrefixCls } = useXProviderContext();

  const prefixCls = getPrefixCls('bubble', customizePrefixCls);
  const listPrefixCls = `${prefixCls}-list`;

  const [wrapCSSVar, hashId, cssVarCls] = useStyle(prefixCls);

  // ============================ Typing ============================
  const [initialized, setInitialized] = React.useState(false);

  React.useEffect(() => {
    setInitialized(true);
    return () => {
      setInitialized(false);
    };
  }, []);

  // ============================= Data =============================
  const mergedData = useListData(items, roles);

  // ============================ Scroll ============================
  // Is current scrollTop at the end. User scroll will make this false.
  const [scrollReachEnd, setScrollReachEnd] = React.useState(true);

  const [updateCount, setUpdateCount] = React.useState(0);

  const onInternalScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
    const target = e.target as HTMLElement;

    setScrollReachEnd(
      target.scrollHeight - Math.abs(target.scrollTop) - target.clientHeight <= TOLERANCE,
    );
  };

  React.useEffect(() => {
    if (autoScroll && listRef.current && scrollReachEnd) {
      listRef.current.scrollTo({
        top: listRef.current.scrollHeight,
      });
    }
  }, [updateCount]);

  // Always scroll to bottom when data change
  React.useEffect(() => {
    if (autoScroll) {
      // When new data arrives, get the third last item key since we've added a new item by default
      const lastItemKey = mergedData[mergedData.length - 3]?.key;
      const bubbleInst = bubbleRefs.current[lastItemKey!];

      // Auto scroll if last 3 item is visible
      if (bubbleInst) {
        const { nativeElement } = bubbleInst;
        const { top, bottom } = nativeElement.getBoundingClientRect();
        const { top: listTop, bottom: listBottom } = listRef.current!.getBoundingClientRect();

        const isVisible = top < listBottom && bottom > listTop;
        if (isVisible) {
          setUpdateCount((c) => c + 1);
          setScrollReachEnd(true);
        }
      }
    }
  }, [mergedData.length]);

  // ========================== Outer Ref ===========================
  React.useImperativeHandle(ref, () => ({
    nativeElement: listRef.current!,
    scrollTo: ({ key, offset, behavior = 'smooth', block }) => {
      if (typeof offset === 'number') {
        // Offset scroll
        listRef.current!.scrollTo({
          top: offset,
          behavior,
        });
      } else if (key !== undefined) {
        // Key scroll
        const bubbleInst = bubbleRefs.current[key];

        if (bubbleInst) {
          // Block current auto scrolling
          const index = mergedData.findIndex((dataItem) => dataItem.key === key);
          setScrollReachEnd(index === mergedData.length - 1);

          // Do native scroll
          bubbleInst.nativeElement.scrollIntoView({
            behavior,
            block,
          });
        }
      }
    },
  }));

  // =========================== Context ============================
  // When bubble content update, we try to trigger `autoScroll` for sync
  const onBubbleUpdate = useEvent(() => {
    if (autoScroll) {
      setUpdateCount((c) => c + 1);
    }
  });

  const context = React.useMemo(
    () => ({
      onUpdate: onBubbleUpdate,
    }),
    [],
  );

  // ============================ Render ============================
  return wrapCSSVar(
    <BubbleContext.Provider value={context}>
      <div
        {...domProps}
        className={classNames(listPrefixCls, rootClassName, className, hashId, cssVarCls, {
          [`${listPrefixCls}-reach-end`]: scrollReachEnd,
        })}
        ref={listRef}
        onScroll={onInternalScroll}
      >
        {mergedData.map(({ key, ...bubble }) => (
          <MemoBubbleListItem
            {...bubble}
            key={key}
            _key={key}
            ref={bubbleRefs}
            typing={initialized ? bubble.typing : false}
          />
        ))}
      </div>
    </BubbleContext.Provider>,
  );
};

const ForwardBubbleList = React.forwardRef(BubbleList);

if (process.env.NODE_ENV !== 'production') {
  ForwardBubbleList.displayName = 'BubbleList';
}

export default ForwardBubbleList;
